import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ReactionQuotientCalculator() {
  const [products, setProducts] = useState("");
  const [reactants, setReactants] = useState("");
  const [kc, setKc] = useState("");
  const [result, setResult] = useState<{ q: number; direction: string } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Reaction Quotient Calculator", url: "https://calclab.app/calculator/reaction-quotient" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Reaction Quotient Calculator",
      "Calculate Q and predict reaction direction. Free online chemistry calculator with instant results and detailed explanations.",
      "https://calclab.app/calculator/reaction-quotient",
      lang
    )
  ]);

  const calculate = () => {
    const prod = parseFloat(products);
    const react = parseFloat(reactants);
    const k = parseFloat(kc);

    if (isNaN(prod) || isNaN(react) || isNaN(k) || react === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Reactants cannot be zero.",
        variant: "destructive",
      });
      return;
    }

    const q = prod / react;
    let direction = "";
    
    if (q < k) {
      direction = "Forward (→) - More products will form";
    } else if (q > k) {
      direction = "Reverse (←) - More reactants will form";
    } else {
      direction = "At Equilibrium - No net change";
    }

    setResult({ q, direction });

    toast({
      title: "Calculation Complete",
      description: `Q = ${q.toExponential(2)}`,
    });
  };

  const reset = () => {
    setProducts("");
    setReactants("");
    setKc("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Reaction Quotient Calculator" 
      calculatorPath="/calculator/reaction-quotient"
      category="chemistry"
      subcategory="Equilibrium"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Reaction Quotient Calculator</CardTitle>
            <CardDescription>
              Calculate Q and predict reaction direction
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="products">[Products] (concentration product)</Label>
                <Input
                  id="products"
                  type="number"
                  step="any"
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  placeholder="Product of product concentrations"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reactants">[Reactants] (concentration product)</Label>
                <Input
                  id="reactants"
                  type="number"
                  step="any"
                  value={reactants}
                  onChange={(e) => setReactants(e.target.value)}
                  placeholder="Product of reactant concentrations"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="kc">Equilibrium Constant (Kc)</Label>
                <Input
                  id="kc"
                  type="number"
                  step="any"
                  value={kc}
                  onChange={(e) => setKc(e.target.value)}
                  placeholder="Enter Kc value"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Reaction Quotient (Q)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{result.q.toExponential(2)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Reaction Direction</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg">{result.direction}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">How It Works</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• If Q &lt; K: Reaction proceeds forward (→)</p>
                <p>• If Q &gt; K: Reaction proceeds reverse (←)</p>
                <p>• If Q = K: System is at equilibrium</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
